import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "antd";
import { ArrowRightOutlined } from "@ant-design/icons";
import { motion, useInView } from "framer-motion";
import type { IconType } from "react-icons";
import {
    FaBell,
    FaChartBar,
    FaComments,
    FaCreditCard,
    FaLock,
    FaMapMarkerAlt,
} from "react-icons/fa";
import { useCountUp } from "../hooks/useCountUp";
import {
    solFadeLeft,
    solFadeRight,
    solScaleIn,
    solSlideUp,
    solStagger,
    solViewport,
} from "../components/solutions/shared/solutionMotion";
// import { ServicePortfolioGallery } from "../components/solutions/shared/ServicePortfolioGallery";
// import {
//     MOBILE_APP_PORTFOLIO_SUBTITLE,
//     mobileAppPortfolioItems,
// } from "../data/servicePortfolioGalleries";
import "../styles/solution-pages-base.css";
import "../styles/service-portfolio.css";
import "../styles/mobile-app-solution.css";

const processSteps = [
    { title: "Requirement Analysis", desc: "Discovery workshops, user stories, and technical scope definition." },
    { title: "UI/UX Design", desc: "Wireframes, interactive prototypes, and brand-aligned interface systems." },
    { title: "Development", desc: "Native and cross-platform builds with scalable, secure architecture." },
    { title: "Testing", desc: "QA cycles, device testing, performance audits, and bug resolution." },
    { title: "Deployment", desc: "Store submission, launch support, and post-release monitoring." },
];

const features: { title: string; icon: IconType }[] = [
    { title: "User Authentication", icon: FaLock },
    { title: "Push Notifications", icon: FaBell },
    { title: "Payment Gateway", icon: FaCreditCard },
    { title: "GPS Tracking", icon: FaMapMarkerAlt },
    { title: "Chat Systems", icon: FaComments },
    { title: "Analytics Dashboard", icon: FaChartBar },
];

const floatingTags = [
    { label: "Notifications", color: "#9e00ff", side: "left" as const, top: "6%", delay: 0 },
    { label: "Payments", color: "#002454", side: "left" as const, top: "36%", delay: 0.4 },
    { label: "Chat", color: "#1A3D6B", side: "left" as const, top: "66%", delay: 0.8 },
    { label: "Profile", color: "#9e00ff", side: "right" as const, top: "14%", delay: 0.2 },
    { label: "Orders", color: "#002454", side: "right" as const, top: "44%", delay: 0.6 },
    { label: "Messages", color: "#7a00a8", side: "right" as const, top: "72%", delay: 1 },
];

const StatBlock = ({ label, value, suffix, active }: { label: string; value: number; suffix: string; active: boolean }) => {
    const count = useCountUp(value, 1800, active);
    return (
        <div className="mad-stat">
            <strong>{count}{suffix}</strong>
            <span>{label}</span>
        </div>
    );
};

export const MobileAppDevelopmentPage = () => {
    const navigate = useNavigate();
    const statsRef = React.useRef<HTMLDivElement>(null);
    const statsInView = useInView(statsRef, solViewport);
    const [activeFeature, setActiveFeature] = useState(0);

    useEffect(() => {
        const timer = setInterval(() => {
            setActiveFeature((p) => (p + 1) % features.length);
        }, 2800);
        return () => clearInterval(timer);
    }, []);

    return (
        <div className="sol-page mad-page">
            {/* Section 1 – Mobile Experience Showcase */}
            <section className="mad-showcase">
                <div className="sol-container mad-showcase__grid">
                    <motion.div
                        className="mad-showcase__copy"
                        initial="hidden"
                        whileInView="visible"
                        viewport={solViewport}
                        variants={solStagger}
                    >
                        <motion.div className="mad-hero-heading" variants={solFadeRight}>
                            <h1 className="mad-hero-title">Mobile App Development</h1>
                            <p className="mad-hero-subtitle">
                                Apps built for <span className="mad-accent">real-world impact</span>
                            </p>
                        </motion.div>
                        <motion.p className="mad-lead" variants={solFadeRight} custom={0.05}>
                            We design and develop high-performance mobile applications that engage users,
                            streamline operations, and scale with your business — on iOS, Android, and beyond.
                        </motion.p>
                        <motion.div className="mad-stats" ref={statsRef} variants={solFadeRight} custom={0.12}>
                            <StatBlock label="Faster Performance" value={85} suffix="%" active={statsInView} />
                            <StatBlock label="Better Engagement" value={72} suffix="%" active={statsInView} />
                            <StatBlock label="Cross Platform Support" value={100} suffix="%" active={statsInView} />
                        </motion.div>
                    </motion.div>

                    <motion.div
                        className="mad-phone-stage"
                        initial="hidden"
                        whileInView="visible"
                        viewport={solViewport}
                        variants={solFadeLeft}
                    >
                        <div className="mad-float-tags" aria-hidden>
                            {floatingTags.map((tag) => (
                                <motion.div
                                    key={tag.label}
                                    className={`mad-float-tag mad-float-tag--${tag.side}`}
                                    style={{
                                        top: tag.top,
                                        animationDelay: `${tag.delay}s`,
                                    }}
                                    initial={{ opacity: 0, scale: 0.92 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={solViewport}
                                    transition={{ delay: 0.15 + tag.delay, duration: 0.45 }}
                                >
                                    <div
                                        className="mad-float-tag__bar"
                                        style={{ background: tag.color }}
                                    />
                                    <span>{tag.label}</span>
                                </motion.div>
                            ))}
                        </div>

                        <div className="mad-phone">
                            <div className="mad-phone__notch" />
                            <div className="mad-phone__screen">
                                <div className="mad-phone__status" />
                                <div className="mad-phone__hero-bar" />
                                <div className="mad-phone__cards">
                                    <div className="mad-phone__mini-card" />
                                    <div className="mad-phone__mini-card" />
                                </div>
                                <div className="mad-phone__nav" />
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Section 2 – App Development Process */}
            <section className="mad-process">
                <div className="sol-container">
                    <motion.div
                        className="mad-section-head"
                        initial="hidden"
                        whileInView="visible"
                        viewport={solViewport}
                        variants={solSlideUp}
                    >
                        <h2 className="mad-h2">App Development Process</h2>
                        <p className="mad-sub">A proven path from concept to launch — transparent at every stage.</p>
                    </motion.div>

                    <div className="mad-timeline">
                        {processSteps.map((step, i) => (
                            <motion.div
                                key={step.title}
                                className="mad-timeline__step"
                                initial="hidden"
                                whileInView="visible"
                                viewport={solViewport}
                                variants={solSlideUp}
                                custom={i * 0.08}
                            >
                                <div className="mad-timeline__marker">
                                    <span>{i + 1}</span>
                                </div>
                                <div className="mad-timeline__content">
                                    <h3>{step.title}</h3>
                                    <p>{step.desc}</p>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Section 3 – Features We Build */}
            <section className="mad-features">
                <div className="sol-container">
                    <motion.div
                        className="mad-section-head mad-section-head--center"
                        initial="hidden"
                        whileInView="visible"
                        viewport={solViewport}
                        variants={solSlideUp}
                    >
                        <h2 className="mad-h2">Features We Build</h2>
                        <p className="mad-sub">Production-ready modules tailored to your product roadmap.</p>
                    </motion.div>

                    <div className="mad-carousel">
                        <motion.div
                            className="mad-carousel__track"
                            animate={{ x: `-${activeFeature * 100}%` }}
                            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                        >
                            {features.map((feature) => {
                                const Icon = feature.icon;
                                return (
                                <div key={feature.title} className="mad-carousel__slide">
                                    <span className="mad-carousel__icon">
                                        <Icon aria-hidden />
                                    </span>
                                    <h3>{feature.title}</h3>
                                </div>
                                );
                            })}
                        </motion.div>
                        <div className="mad-carousel__dots">
                            {features.map((f, i) => (
                                <button
                                    key={f.title}
                                    type="button"
                                    className={`mad-carousel__dot${activeFeature === i ? " is-active" : ""}`}
                                    onClick={() => setActiveFeature(i)}
                                    aria-label={f.title}
                                />
                            ))}
                        </div>
                    </div>

                    <motion.div
                        className="mad-feature-strip"
                        initial="hidden"
                        whileInView="visible"
                        viewport={solViewport}
                        variants={solStagger}
                    >
                        {features.map((f) => {
                            const Icon = f.icon;
                            return (
                            <motion.span key={f.title} className="mad-feature-pill" variants={solScaleIn}>
                                <Icon aria-hidden /> {f.title}
                            </motion.span>
                            );
                        })}
                    </motion.div>
                </div>
            </section>

            {/* Our Work section — temporarily hidden
            <ServicePortfolioGallery
                items={mobileAppPortfolioItems}
                subtitle={MOBILE_APP_PORTFOLIO_SUBTITLE}
                sectionId="mad-portfolio"
            />
            */}

            <section className="sol-cta">
                <div className="sol-container">
                    <motion.div
                        className="sol-cta__inner"
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={solViewport}
                    >
                        <h2 className="sol-cta__title">Ready to launch your mobile app?</h2>
                        <p className="sol-cta__desc">
                            Share your idea — we&apos;ll map the build, timeline, and technology stack for your product.
                        </p>
                        <Button className="sol-btn" onClick={() => navigate("/contact")}>
                            Start Your App Project <ArrowRightOutlined />
                        </Button>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};
